import {
  Component,
  computed,
  HostBinding,
  inject,
  model,
  OnDestroy,
  signal,
  TemplateRef,
  ViewChild,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem } from '@components/abb-actions-menu/abb-actions-menu.component';
import { AbbStatusIconComponent } from '@components/abb-status-icon/abb-status-icon.component';
import { AbbSwitchComponent } from '@components/abb-switch/abb-switch.component';
import {
  AbbTableComponent,
  DataTableFilter,
  IColumn,
} from '@components/abb-table/abb-table.component';
import {
  alarmListQuery,
  deleteAlarmsMutation,
  toggleAlarmsEnabledCtrlMutation,
} from '@graphql/alarm';
import { groupSelectOptionsWImageQuery } from '@graphql/group';
import { propertySelectOptionsWImageQuery } from '@graphql/property';
import { TranslateService } from '@ngx-translate/core';
import { AlarmsSidePanelComponent } from '@pages/alarms/alarms-side-panel/alarms-side-panel.component';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { OverlayService } from '@services/ui/overlay.service';
import { dataProviderFromObservable } from '@utils/data-provider';
import { SELECTION } from '@utils/table';
import { Apollo } from 'apollo-angular';
import { AbbCommonModule } from 'app/abb-common.module';
import {
  AlarmCategory,
  AlarmListQuery,
  AlarmListQueryVariables,
  DeleteAlarmsMutation,
  DeleteAlarmsMutationVariables,
  GroupSelectOptionsWImageQuery,
  PropertySelectOptionsWImageQuery,
  Status,
  ToggleAlarmsEnabledCtrlMutation,
  ToggleAlarmsEnabledCtrlMutationVariables,
} from 'gql/graphql';
import { BehaviorSubject, first, Subscription } from 'rxjs';
import { AbbDateComponent } from '@components/abb-date/abb-date.component';
import { normalizeDateOnly } from '@utils/datetime';
import { ToastService } from '@services/ui/toast.service';
import { ALARMS_SUBTITLE } from '@pages/alarms/alarms';
import { capitalizeFirstLetter, t } from '@utils/strings';
import { LOADING } from '@utils/loader';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { PAGES } from '@constants/constants';
import { PermissionsService } from '@services/user/permissions.service';

type AlarmsDataTableFilter = {
  status: string | null;
  category: AlarmCategory | null;
  name: string;
  alarmEnabled: boolean | null;
  date: Date | null;
};

@Component({
  selector: 'abb-alarms-list',
  standalone: true,
  imports: [
    AbbCommonModule,
    TPipe,
    AbbSwitchComponent,
    FormsModule,
    AbbTableComponent,
    AbbStatusIconComponent,
    AbbDateComponent,
    OverflowTooltipDirective,
  ],
  template: `
    <abb-table
      [columns]="columns()"
      [dataProvider]="dataProvider"
      [defaultFilters]="defaultFilters"
      [filters]="filters()"
      [loading]="loading()"
      [selectable]="canEdit()"
      [actions]="actions"
      [defaultSortBy]="[
        { column: 'status', order: 'desc' },
        { column: 'name', order: 'asc' }
      ]"
    ></abb-table>
    <ng-template #status let-item>
      <div class="flex gap-2 items-center">
        <abb-status-icon
          variant="outlined"
          size="small"
          [status]="item.status"
        ></abb-status-icon>
        <span overflowTooltip class="block truncate">
          {{
            (item.status === Status.Ok
              ? 'Okay'
              : item.status === Status.Warning
              ? 'Offline'
              : 'In alarm'
            ) | t
          }}
        </span>
      </div>
    </ng-template>
    <ng-template #enabled let-item>
      <div class="flex justify-center items-center">
        <abb-switch
          id="toggle-alarm"
          [disabled]="!canEdit()"
          [(ngModel)]="alarmsEnabled()[item.id]"
          (click)="toggleAlarm(item.id, $event)"
        ></abb-switch>
      </div>
    </ng-template>
    <!-- <ng-template #properties let-item>
      <span overflowTooltip class="block truncate">
        @if (item.propertiesCriteria) {
          {{ getPropertyNameById(item.propertiesCriteria) }}
        }
      </span>
    </ng-template> -->
    <ng-template #devices let-item>
      <span overflowTooltip class="block truncate">
        @for (d of item.devices; track d.name) {
        {{ d.name }}@if (!$last) {, } }
      </span>
    </ng-template>
    <!-- <ng-template #groups let-item>
      <span overflowTooltip class="block truncate">
        @if (item.groupsCriteria) {
          {{ getGroupNameById(item.groupsCriteria) }}
        }
      </span>
    </ng-template> -->
    <ng-template #lastUpdate let-item>
      <abb-date
        [date]="item.lastUpdate"
        [showTime]="true"
        [showTooltip]="true"
      ></abb-date>
    </ng-template>
  `,
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'alarms.tabs.list' }],
})
export class AlarmsListComponent implements OnDestroy {
  readonly Status = Status;
  @HostBinding('attr.id') componentid = 'alarms-list-component';
  @ViewChild(AbbTableComponent) tableComponent!: AbbTableComponent<any, any>;
  prefix = inject(TRANSLATION_PREFIX);
  subtitle = inject(ALARMS_SUBTITLE);
  canEdit = computed(() => this.permissionsService.canEditPage(PAGES.ALARMS));

  enabledTemplate = viewChild<TemplateRef<unknown>>('enabled');
  propertiesTemplate = viewChild<TemplateRef<unknown>>('properties');
  groupsTemplate = viewChild<TemplateRef<unknown>>('groups');
  devicesTemplate = viewChild<TemplateRef<unknown>>('devices');
  statusTemplate = viewChild<TemplateRef<unknown>>('status');
  lastUpdateTemplate = viewChild<TemplateRef<unknown>>('lastUpdate');

  alarmsEnabled = model<Record<string, boolean | undefined>>({});
  lang = signal('');
  loading = signal(true);
  alarms$ = new BehaviorSubject<AlarmListQuery['alarmList']>([]);
  properties = signal<Array<{ id: string; name: string }>>([]);
  groups = signal<Array<{ id: string; name: string }>>([]);

  dataProvider = dataProviderFromObservable<
    AlarmListQuery['alarmList'][number],
    AlarmsDataTableFilter
  >(
    this.alarms$,
    (el, filter) => {
      return (
        el.name.toLowerCase().includes(filter.name.toLowerCase()) &&
      (!filter.status || el.status === filter.status) &&
      (!filter.category || el.category === filter.category) &&
      (!filter.alarmEnabled || el.enabled === filter.alarmEnabled) &&
      (!filter.date ||
        (filter.date
          ? normalizeDateOnly(el.lastUpdate) === normalizeDateOnly(filter.date)
          : true))
    );
    },
    (a, b, cols) => {
      for (let c of cols) {
        let itemA, itemB;
        if (c.column === 'name') {
          itemA = a.name.toLowerCase();
          itemB = b.name.toLowerCase();
        } else {
          itemA = a.status;
          itemB = b.status;
        }

        if (itemA < itemB) {
          return c.order === 'asc' ? -1 : 1;
        }
        if (itemA > itemB) {
          return c.order === 'asc' ? 1 : -1;
        }
      }

      return 0;
    }
  );
  columns = computed<IColumn<AlarmListQuery['alarmList'][number]>[]>(() => {
    return [
      {
        id: 'name',
        name: 'Name',
        key: 'name',
        translate: true,
        tableCellClass: 'font-medium',
      },
      {
        id: 'status',
        name: 'Status',
        templateRef: this.statusTemplate(),
        translate: true,
      },
      {
        id: 'condition',
        name: 'Condition',
        key: 'condition',
        translate: true,
      },
      {
        id: 'lastUpdate',
        name: 'Last update',
        translate: true,
        templateRef: this.lastUpdateTemplate(),
      },
      {
        id: 'enabled',
        name: 'Enabled',
        templateRef: this.enabledTemplate(),
        translate: true,
        tableCellClass: 'font-medium',
        tableHeaderCellClass: '[&>*]:!justify-center',
      },
      {
        id: 'devices',
        name: 'Devices',
        templateRef: this.devicesTemplate(),
        translate: true,
      },
    ];
  });
  filters = computed<DataTableFilter<AlarmsDataTableFilter>[]>(() => {
    return [
      {
        id: 'name',
        filterType: 'text',
        className: 'w-full',
        label: 'Name',
      },
      {
        id: 'status',
        filterType: 'select',
        className: 'w-full',
        label: 'Status',
        filterOptions: [
          {
            label: 'Okay',
            value: Status.Ok,
            skipTranslation: false,
          },
          {
            label: 'Offline',
            value: Status.Warning,
            skipTranslation: false,
          },
          {
            label: 'In alarm',
            value: Status.Alarm,
            skipTranslation: false,
          },
        ],
      },
      {
        id: 'category',
        filterType: 'select',
        className: 'w-full',
        label: 'Category',
        filterOptions: [
          {
            label: 'Critical',
            value: AlarmCategory.Critical,
            skipTranslation: false,
          },
          {
            label: 'Warning',
            value: AlarmCategory.Warning,
            skipTranslation: false,
          }
        ],
      },
      {
        id: 'date',
        filterType: 'date',
        className: 'w-full',
        label: 'Date',
      },
      {
        id: 'alarmEnabled',
        filterType: 'switch',
        className: 'w-full inline-flex justify-start items-center',
        label: 'Show with alarm only',
      },
    ];
  });
  defaultFilters: AlarmsDataTableFilter = {
    status: null,
    category: null,
    name: '',
    alarmEnabled: false,
    date: null,
  };
  actions: Array<MenuItem<AlarmListQuery['alarmList'][number]>> = [
    {
      icon: 'mat-outlined/check_circle',
      label: 'Enable',
      action: (items) => this.toggleAllAlarms(items, { enable: true }),
      selectionAllowed: SELECTION.MULTIPLE,
    },
    {
      icon: 'mat-outlined/block',
      label: 'Disable',
      action: (items) => this.toggleAllAlarms(items, { enable: false }),
      selectionAllowed: SELECTION.MULTIPLE,
    },
    {
      icon: 'mat-outlined/edit',
      label: 'Edit',
      action: (items) => this.openSidePanel(items[0]),
      selectionAllowed: SELECTION.SINGLE,
    },
    {
      icon: 'mat-outlined/delete',
      label: 'Delete',
      action: (item) => this.openDeleteModal(item),
      selectionAllowed: SELECTION.ALWAYS,
    },
  ];

  private readonly subscriptions: Subscription[] = [];
  constructor(
    private readonly translate: TranslateService,
    private readonly overlayService: OverlayService,
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly permissionsService: PermissionsService
  ) {
    this.lang.set(translate.currentLang);
    this.subscriptions.push(
      apollo
        .watchQuery<AlarmListQuery, AlarmListQueryVariables>({
          query: alarmListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            this.alarms$.next(data.alarmList);
            this.loading.set(loading);
            this.alarmsEnabled.set(
              data.alarmList.reduce((acc, curr) => {
                return {
                  ...acc,
                  [curr.id]: curr.enabled,
                };
              }, {})
            );
            this.subtitle.set({
              key: 'You have a total of {tot} alarms, {totAlerts} of which requires your attention',
              params: {
                tot: data.alarmList?.length,
                totAlerts: data.alarmList?.filter(
                  (alarm) => alarm.status === Status.Alarm
                )?.length,
              },
            });
          },
          error: (err) => {
            this.loading.set(false);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<PropertySelectOptionsWImageQuery>({
          query: propertySelectOptionsWImageQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.properties.set(data.propertyList || []);
          },
          error: (err) => {
          },
        }),
      apollo
        .watchQuery<GroupSelectOptionsWImageQuery>({
          query: groupSelectOptionsWImageQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.groups.set(data.groupList || []);
          },
          error: (err) => {
          },
        })
    );
  }

  /* filterAlarms(event: MouseEvent) {
    const filteredAlarms = this.alarms$.value?.filter(
      (alarm) => alarm.status === Status.Alarm
    );
    this.alarms$.next(filteredAlarms);
  } */

  openSidePanel(item: AlarmListQuery['alarmList'][number]) {
    const sidePanel = this.overlayService.openSidePanel(
      AlarmsSidePanelComponent,
      {
        mode: 'edit',
        defaultData: item,
      }
    );

    return new Promise<void>((resolve, reject) => {
      sidePanel.componentInstance.onSubmitted.subscribe((success) => {
        if (success) {
          resolve();
        } else {
          reject();
        }
      });
    });
  }

  toggleAllAlarms(
    items: AlarmListQuery['alarmList'],
    opts: { enable: boolean }
  ) {
    return new Promise<void>((resolve, reject) => {
      const alarmsToToggle = items.filter((i) => {
        if (opts.enable) {
          return !i.enabled;
        }
        return i.enabled;
      });
      const action = opts.enable ? 'enable' : 'disable';
      if (alarmsToToggle && alarmsToToggle.length === 0) {
        this.toast.notifyWarning(
          t(
            this.prefix,
            `Selected alarms are already {status}`,
            this.translate,
            {
              status: t(
                this.prefix,
                opts.enable ? 'enabled_other' : 'disabled_other',
                this.translate
              ),
            }
          )
        );
      } else {
        this.openConfirmModal(action)
          .then(() => {
            if (alarmsToToggle) {
              this.awaitingRes.set(true);
              this.updateAlarmsEnabled(
                alarmsToToggle.map((a) => ({
                  id: a.id,
                  enabled: opts.enable,
                }))
              )
                .then(resolve)
                .finally(() => {
                  this.apollo.client.refetchQueries({
                    include: ['AlarmList']
                  })
                  this.awaitingRes.set(false)
                });
            }
          })
          .catch((err) => {
            reject(new Error(err));
          });
      }
    });
  }

  toggleAlarm(id: string, event: Event) {
    if (this.canEdit()) {
      event.preventDefault();
      const row = this.alarms$.value?.find((c) => c.id === id);
      const newValue = !row?.enabled;
      if (newValue !== undefined && newValue !== row?.enabled) {
        const action = newValue ? 'enable' : 'disable';
        this.openConfirmModal(action, row)
          .then(() => {
            this.awaitingRes.set(true);
            this.updateAlarmsEnabled([
              {
                id,
                enabled: newValue,
              },
            ]).finally(() => {
              this.tableComponent?.selection?.clear();
              this.apollo.client.refetchQueries({
                include: ['AlarmList']
              })
              this.awaitingRes.set(false);
            });
          })
          .catch(() => console.log('cancel'));
      }
    }
  }

  updateAlarmsEnabled(input: { id: string; enabled: boolean }[]) {
    return new Promise<void>((resolve, reject) =>
      this.apollo
        .mutate<
          ToggleAlarmsEnabledCtrlMutation,
          ToggleAlarmsEnabledCtrlMutationVariables
        >({
          mutation: toggleAlarmsEnabledCtrlMutation,
          variables: {
            input,
          },
        })
        .pipe(first())
        .subscribe({
          next: () => {
            resolve();
            this.toast.notifySuccess(
              t(this.prefix, `Alarm {action} successfully`, this.translate, {
                action: input.some((i) => i.enabled)
                  ? t(this.prefix, 'enabled', this.translate)
                  : t(this.prefix, 'disabled', this.translate),
              })
            );
            this.apollo.client.refetchQueries({
              include: ['AlarmList']
            })
          },
          error: () => {
            reject();
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while {action} alarm`,
                this.translate,
                {
                  action: input.some((i) => i.enabled)
                    ? t(this.prefix, 'enabling', this.translate)
                    : t(this.prefix, 'disabling', this.translate),
                }
              )
            );
          },
        })
    );
  }

  openConfirmModal(
    action: string,
    row?: AlarmListQuery['alarmList'][number] | undefined
  ) {
    return this.overlayService.openAlert({
      message: `${t(
        this.prefix,
        'Are you sure that you want to {action}',
        this.translate,
        { action: t(this.prefix, action, this.translate) }
      )} ${
        row
          ? `<b>${row?.name}</b>`
          : t(this.prefix, 'all selected items', this.translate)
      }?`,
      htmlMessage: true,
      confirmText: t(
        this.prefix,
        capitalizeFirstLetter(action),
        this.translate
      ),
    });
  }

  awaitingRes = inject(LOADING);
  openDeleteModal(items: AlarmListQuery['alarmList']) {
    return new Promise<void>((resolve, reject) => {
      this.openConfirmModal('remove', items.length > 1 ? undefined : items[0])
        .then(() => {
          this.awaitingRes.set(true);
          this.deleteAlarms(items.map((a) => a.id))
            .then(resolve)
            .finally(() => {
              this.apollo.client.refetchQueries({
                include: ['AlarmList']
              })
              this.awaitingRes.set(false)
            });
        })
        .catch((err) => {
          console.log('cancel');
          reject(err);
        });
    });
  }

  deleteAlarms(ids: string | string[]) {
    return new Promise<void>((resolve, reject) =>
      this.apollo
        .mutate<DeleteAlarmsMutation, DeleteAlarmsMutationVariables>({
          mutation: deleteAlarmsMutation,
          variables: {
            ids,
          },
          refetchQueries: [{ query: alarmListQuery }],
          awaitRefetchQueries: true,
        })
        .pipe(first())
        .subscribe({
          next: () => {
            resolve();
            this.toast.notifySuccess(
              t(this.prefix, `Alarm deleted successfully`, this.translate)
            );
          },
          error: () => {
            reject();
            this.toast.notifySuccess(
              t(
                this.prefix,
                `An error occurred while deleting the alarm`,
                this.translate
              )
            );
          },
        })
    );
  }

  getPropertyNameById(id: string): string {
    const property = this.properties().find(p => p.id === id);
    return property?.name || id;
  }

  getGroupNameById(id: string): string {
    const group = this.groups().find(g => g.id === id);
    return group?.name || id;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
